"use client";

/**
 * Uncovers streamed skeletons before React hydrates. The script itself and
 * React's hidden stream containers are not visible content: wait for a drawn
 * skeleton or content element, then recheck on the following animation frame.
 * React and the native timeout remain fallbacks. Ordinary browsers do no work.
 *
 * Android must NOT wait on animation frames. The held system splash
 * (MainActivity's `setKeepOnScreenCondition`) is an OnPreDrawListener that
 * cancels every draw of the content view, and an Android WebView only gets
 * BeginFrames from its own draw pass — so `requestAnimationFrame` never fires
 * while the splash is up, and a frame-gated signal could only ever arrive
 * after the native 3.5s cap had already released it. Android therefore polls
 * layout on a timer: nothing can paint under its splash anyway, and the draw
 * that follows the release is the same one the cap triggers today (WebView
 * background is the splash color, so it is never white). iOS keeps frames —
 * its WKWebView renders beneath a UIView overlay, so a frame there really is
 * a painted skeleton — but races each one against a 100ms timer, so a stalled
 * frame loop can only delay the release by that much, never to the 3.5s cap.
 */
const EARLY_SIGNAL_SCRIPT = `(function(){
  var w=window.webkit,c=window.Capacitor;
  var ios=w&&w.messageHandlers&&w.messageHandlers.hiiveSplashReady;
  var android=c&&c.Plugins&&c.Plugins.HiiveSplash;
  var nativeAndroid=c&&c.getPlatform&&c.getPlatform()==="android"&&c.nativePromise;
  if(!ios&&!(android&&android.ready)&&!nativeAndroid)return;
  if(ios&&typeof requestAnimationFrame==="undefined")return;
  function next(callback){
    if(!ios){setTimeout(callback,32);return;}
    var done=false;function run(){if(done)return;done=true;callback();}
    requestAnimationFrame(run);setTimeout(run,100);
  }
  var deadline=performance.now()+3500;
  function visible(){
    var nodes=document.querySelectorAll('[data-slot="skeleton"],h1,h2,p,button,img,svg');
    for(var i=0;i<nodes.length;i++){
      var el=nodes[i],rect=el.getBoundingClientRect();
      if(rect.width<=1||rect.height<=1)continue;
      if(el.checkVisibility&&!el.checkVisibility({checkOpacity:true,checkVisibilityCSS:true}))continue;
      var style=window.getComputedStyle(el);
      if(style.visibility==="visible"&&style.opacity!=="0")return true;
    }
    return false;
  }
  function signal(){
    try{if(ios){var sent=ios.postMessage("ready");if(sent&&sent.catch)sent.catch(function(){});}}catch(e){}
    try{var result;if(android&&android.ready)result=android.ready();else if(nativeAndroid)result=c.nativePromise("HiiveSplash","ready",{});if(result&&result.catch)result.catch(function(){});}catch(e){}
  }
  function poll(){
    if(performance.now()>=deadline)return;
    if(visible())next(function(){if(visible())signal();else poll();});
    else next(poll);
  }
  next(poll);
})();`;

export function NativeSplashEarlySignal() {
  return (
    <script
      // Parser-inserted scripts execute during HTML streaming, before
      // hydration — that timing is the entire point, so this must stay an
      // inline script rather than next/script or an external file.
      // biome-ignore lint/security/noDangerouslySetInnerHtml: static compile-time constant, no user input.
      dangerouslySetInnerHTML={{ __html: EARLY_SIGNAL_SCRIPT }}
    />
  );
}
